"use client";

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Cookie, X, Check, Settings } from "lucide-react";
import Link from "next/link";

const CONSENT_KEY = "ccmt_cookie_consent";

export function CookieConsent() {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Delay check to avoid hydration mismatch
    const timer = setTimeout(() => {
      try {
        const consent = localStorage.getItem(CONSENT_KEY);
        if (!consent) {
          setIsVisible(true);
        }
      } catch {
        // localStorage not available
      }
    }, 1500);
    return () => clearTimeout(timer);
  }, []);

  const acceptAll = () => {
    try {
      localStorage.setItem(CONSENT_KEY, "all");
    } catch {}
    setIsVisible(false);
  };

  const acceptNecessary = () => {
    try {
      localStorage.setItem(CONSENT_KEY, "necessary");
    } catch {}
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 120, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 120, opacity: 0 }}
          transition={{ type: "spring", stiffness: 200, damping: 25 }}
          className="fixed bottom-0 left-0 right-0 z-[9999] p-4 md:p-6"
          role="dialog"
          aria-label="Cookie consent"
          aria-live="polite"
        >
          <div className="max-w-5xl mx-auto bg-surface-container border border-outline-variant/30 rounded-2xl shadow-2xl shadow-black/20 backdrop-blur-md p-5 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
            {/* Icon */}
            <div className="flex-shrink-0 size-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
              <Cookie className="size-5" />
            </div>

            {/* Text */}
            <div className="flex-1 min-w-0">
              <p className="font-bold text-foreground text-sm mb-1">
                We use cookies to improve your experience
              </p>
              <p className="text-on-surface-variant text-xs leading-relaxed">
                We use cookies and similar technologies, including Google Analytics and Google AdSense, to understand how you use our site and to serve relevant ads. By clicking &quot;Accept All,&quot; you consent to this use. You can also accept only necessary cookies or learn more in our{" "}
                <Link
                  href="/privacy-policy"
                  className="text-primary font-semibold hover:underline"
                  onClick={() => setIsVisible(false)}
                >
                  Privacy Policy
                </Link>
                .
              </p>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-2 flex-shrink-0 w-full md:w-auto">
              <button
                onClick={acceptNecessary}
                className="flex-1 md:flex-none flex items-center justify-center gap-1.5 h-9 px-4 rounded-xl border border-outline-variant/30 text-on-surface-variant text-xs font-bold hover:bg-surface-container-high transition-colors"
                aria-label="Accept necessary cookies only"
              >
                <Settings className="size-3.5" />
                Necessary Only
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 md:flex-none flex items-center justify-center gap-1.5 h-9 px-5 rounded-xl bg-primary text-primary-foreground text-xs font-bold hover:opacity-90 active:scale-95 transition-all"
                aria-label="Accept all cookies"
              >
                <Check className="size-3.5" />
                Accept All
              </button>
              <button
                onClick={acceptNecessary}
                className="flex-shrink-0 size-9 rounded-xl border border-outline-variant/20 text-on-surface-variant hover:text-foreground hover:bg-surface-container-high flex items-center justify-center transition-colors"
                aria-label="Close cookie banner"
              >
                <X className="size-4" />
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
